import { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { KTSVG } from '../../_metronic/helpers'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'

interface InterBuLine {
	idSalesInvoiceItems: number
	idSalesOrderRows: number
	productName: string
	packSize: string
	quantity: number
	unitName: string
	rate: number
	amount: number
	interBuQty: string
	interBuUnitRate: string
}

const numberFormat = (value: number) => {
	return value.toLocaleString('en-US', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

const roundTo = (value: number, places: number) => Math.round(value * 10 ** places) / 10 ** places

// A saved decimal ("24.000") as the input shows it ("24"), or empty when nothing is saved.
const savedInput = (value: any) => (value === null || value === undefined ? '' : String(parseFloat(value)))

function SalesInvoiceInterBu() {
	const { idSalesInvoice }: { idSalesInvoice: string } = useParams()
	const [lines, setLines] = useState<InterBuLine[]>([])
	const [invoiceNo, setInvoiceNo] = useState('')
	const [clientName, setClientName] = useState('')
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'sales-invoice/' + idSalesInvoice, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const items = resp.data?.items || []
				const salesOrder = resp.data?.sales_order || resp.data?.salesOrder || {}
				setInvoiceNo(resp.data?.invoiceId || '')
				setClientName(salesOrder.company?.company_name || '')
				setLines(items.map((item: any) => {
					const orderRow = item.sales_order_row || item.salesOrderRow || {}
					const product = orderRow.product || {}
					const packUnit = product.item?.unit?.unit_name || ''

					return {
						idSalesInvoiceItems: item.id_sales_invoice_items,
						idSalesOrderRows: orderRow.id_sales_order_rows || orderRow.idSalesOrderRows,
						productName: orderRow.alternate_name?.alternate_name
							|| orderRow.alternateName?.alternate_name
							|| product.product_name
							|| product.productName
							|| '',
						packSize: `${product.packing || ''} ${packUnit}`.trim(),
						quantity: parseFloat(item.delivered_quantity ?? item.deliveredQuantity ?? 0),
						unitName: product.unit?.unit_name || product.unit?.unitName || '',
						rate: parseFloat(orderRow.unit_rate ?? orderRow.unitRate ?? 0),
						amount: parseFloat(item.delivered_amount ?? item.deliveredAmount ?? 0),
						interBuQty: savedInput(item.inter_bu_qty),
						interBuUnitRate: savedInput(item.inter_bu_unit_rate),
					}
				}).filter((line: InterBuLine) => line.quantity > 0))
				setLoading(false)
			})
			.catch(() => {
				setLines([])
				setLoading(false)
			})
	}, [idSalesInvoice])

	// Inter BU Rate = (Inter BU Qty x Inter BU Unit Rate) / Quantity, rounded as the server saves it; null until both are entered.
	const interBuRate = (line: InterBuLine) => {
		const qty = parseFloat(line.interBuQty)
		const unitRate = parseFloat(line.interBuUnitRate)
		if (line.interBuQty === '' || line.interBuUnitRate === '' || isNaN(qty) || isNaN(unitRate) || qty < 0 || unitRate < 0) {
			return null
		}
		return roundTo(roundTo(qty, 3) * roundTo(unitRate, 2) / line.quantity, 2)
	}

	const isValidRate = (line: InterBuLine) => {
		const rate = interBuRate(line)
		return rate !== null && rate <= line.rate
	}

	const interBuAmount = (line: InterBuLine) => {
		const rate = interBuRate(line)
		if (rate === null || !isValidRate(line)) {
			return null
		}
		return Math.min(roundTo(rate * line.quantity, 2), line.amount)
	}

	const canPrint = lines.length > 0 && lines.every(isValidRate)

	const totalAmount = lines.reduce((sum, line) => sum + line.amount, 0)
	const interBuTotal = lines.reduce((sum, line) => sum + (interBuAmount(line) ?? 0), 0)

	const handleLineChange = (index: number, field: 'interBuQty' | 'interBuUnitRate', value: string) => {
		setLines((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], [field]: value }
			return updated
		})
	}

	// Saves every line and answers with the saved Inter BU Rate of each sales order row, or null when it failed.
	const saveRates = async (): Promise<Record<number, number> | null> => {
		setSaving(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}sales-invoice/inter-bu-rates`,
				postRequestOptions({
					idSalesInvoice,
					rows: lines.map((line) => ({
						idSalesInvoiceItems: line.idSalesInvoiceItems,
						interBuQty: line.interBuQty,
						interBuUnitRate: line.interBuUnitRate,
					})),
				})
			)
			const respData = await response.json()
			if (respData.success !== true) {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					html: validationErrorMsg(respData.errors ?? respData.errorMessage ?? respData.message).join('<br>'),
				})
				return null
			}

			const savedRates: Record<number, number> = {}
			respData.data.forEach((row: any) => {
				savedRates[row.idSalesOrderRows] = row.interBuRate
			})
			return savedRates
		} catch (error) {
			console.log(error, 'catch the hoop')
			Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save the Inter BU rates.' })
			return null
		} finally {
			setSaving(false)
		}
	}

	const handleSave = async () => {
		if (!canPrint) {
			return
		}
		if (await saveRates()) {
			Swal.fire({ icon: 'success', title: 'Saved', text: 'Inter BU rates saved.' })
		}
	}

	const handlePrint = async (part: 'entered' | 'remaining') => {
		if (!canPrint) {
			return
		}

		// Open the tab before saving: one opened after waiting for the save is blocked as a popup.
		const printWindow = window.open('', '_blank')
		const savedRates = await saveRates()
		if (!savedRates) {
			printWindow?.close()
			return
		}

		const params = new URLSearchParams()
		lines.forEach((line) => {
			params.append(`interBuRates[${line.idSalesOrderRows}]`, String(savedRates[line.idSalesOrderRows]))
		})
		params.append('interBuPart', part)
		const url = `${process.env.REACT_APP_BACKEND}invoiceDownload/${idSalesInvoice}?${params.toString()}`

		if (printWindow) {
			printWindow.opener = null
			printWindow.location.href = url
		} else {
			window.open(url, '_blank', 'noopener,noreferrer')
		}
	}

	return (
		<div className='card card-custom'>
			<div className='card-header'>
				<div className="card-title">
					<KTSVG path='/media/svg/icons/Devices/Printer.svg' className='svg-icon-1 svg-icon-primary' />
					<span className='text fw-bolder' style={{ marginLeft: '10px' }}>
						Inter BU Invoice {invoiceNo && <>- {invoiceNo}</>}
					</span>
				</div>
				<div className="card-toolbar">
					<Link to='/sales-invoices' className="btn btn-sm btn-secondary">Back to Sales Invoices</Link>
				</div>
			</div>

			<div className='card-body'>
				{loading && <div>Loading...</div>}
				{!loading && (
					<>
						{clientName && <p className="mb-4"><b>Client:</b> {clientName}</p>}
						<p className="text-muted">
							Enter the Inter BU quantity and unit rate for each product: the Inter BU Rate is <b>(Inter BU Qty × Inter BU Unit Rate) ÷ Quantity</b>. Invoice 1 prints with the Inter BU Rate and Invoice 2 prints the total remaining amount as a single <b>Transportation and loading unloading cost</b> item. The quantities and rates are saved when you save or print.
						</p>
						<Table size="sm" bordered hover responsive>
							<thead>
								<tr>
									<th>SN</th>
									<th>Product</th>
									<th>Pack Size</th>
									<th className="text-end">Quantity</th>
									<th>Unit</th>
									<th className="text-end">Rate</th>
									<th className="text-end">Amount</th>
									<th className="text-end">Inter BU Qty</th>
									<th className="text-end">Inter BU Unit Rate</th>
									<th className="text-end">Inter BU Rate</th>
									<th className="text-end">Invoice 1 Amount</th>
									<th className="text-end">Remaining Rate</th>
									<th className="text-end">Invoice 2 Amount</th>
								</tr>
							</thead>
							<tbody>
								{lines.map((line, index) => {
									const amount = interBuAmount(line)
									const rate = interBuRate(line)
									const isEntered = line.interBuQty !== '' && line.interBuUnitRate !== ''
									const hasError = isEntered && !isValidRate(line)
									return (
										<tr key={line.idSalesOrderRows || index}>
											<td>{index + 1}</td>
											<td>{line.productName}</td>
											<td>{line.packSize}</td>
											<td className="text-end">{line.quantity}</td>
											<td>{line.unitName}</td>
											<td className="text-end">{numberFormat(line.rate)}</td>
											<td className="text-end">{numberFormat(line.amount)}</td>
											<td style={{ minWidth: '110px' }}>
												<input
													type="number"
													min={0}
													step="0.001"
													className="form-control form-control-sm text-end"
													value={line.interBuQty}
													onChange={(event) => handleLineChange(index, 'interBuQty', event.target.value)}
												/>
											</td>
											<td style={{ minWidth: '120px' }}>
												<input
													type="number"
													min={0}
													step="0.01"
													className="form-control form-control-sm text-end"
													value={line.interBuUnitRate}
													onChange={(event) => handleLineChange(index, 'interBuUnitRate', event.target.value)}
												/>
											</td>
											<td style={{ minWidth: '140px' }}>
												<input
													type="text"
													readOnly
													tabIndex={-1}
													className={`form-control form-control-sm form-control-solid text-end ${hasError ? 'is-invalid' : ''}`}
													value={rate === null ? '' : numberFormat(rate)}
												/>
												{hasError && (
													<div className="invalid-feedback">
														{rate === null ? 'Enter 0 or more' : `Must not be more than ${numberFormat(line.rate)}`}
													</div>
												)}
											</td>
											<td className="text-end">{amount === null ? '' : numberFormat(amount)}</td>
											<td className="text-end">{amount === null || rate === null ? '' : numberFormat(line.rate - rate)}</td>
											<td className="text-end">{amount === null ? '' : numberFormat(line.amount - amount)}</td>
										</tr>
									)
								})}
								{lines.length === 0 && (
									<tr>
										<td colSpan={13} className="text-center text-muted">No products found on this invoice.</td>
									</tr>
								)}
							</tbody>
							{lines.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={6} className="text-end">Total</th>
										<th className="text-end">{numberFormat(totalAmount)}</th>
										<th colSpan={3}></th>
										<th className="text-end">{numberFormat(interBuTotal)}</th>
										<th></th>
										<th className="text-end">{canPrint ? numberFormat(totalAmount - interBuTotal) : ''}</th>
									</tr>
								</tfoot>
							)}
						</Table>

						<div className="d-flex justify-content-end mt-4">
							<button type="button" className="btn btn-sm btn-success me-2" onClick={handleSave} disabled={!canPrint || saving}>
								Save
							</button>
							<button type="button" className="btn btn-sm btn-primary me-2" onClick={() => handlePrint('entered')} disabled={!canPrint || saving}>
								Print Invoice 1 (Inter BU Rate)
							</button>
							<button type="button" className="btn btn-sm btn-primary" onClick={() => handlePrint('remaining')} disabled={!canPrint || saving}>
								Print Invoice 2 (Remaining Rate)
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default SalesInvoiceInterBu
